import { useState, useEffect, useCallback } from 'react';
import { promptAPI } from '../services/api';
import { Prompt, SearchFilters, PromptListResponse } from '../types';
import toast from 'react-hot-toast';

export const usePrompts = (initialFilters: SearchFilters = {}) => {
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  });
  const [filters, setFilters] = useState<SearchFilters>(initialFilters);

  // Fetch prompts
  const fetchPrompts = useCallback(async (newFilters?: SearchFilters) => {
    setLoading(true);
    setError(null);
    
    try {
      const currentFilters = newFilters || filters;
      const response: PromptListResponse = await promptAPI.getPrompts(currentFilters);
      
      setPrompts(response.prompts);
      setPagination(response.pagination);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '프롬프트를 불러오는데 실패했습니다.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Search prompts
  const searchPrompts = useCallback((searchQuery: string) => {
    const newFilters = { ...filters, search: searchQuery, page: 1 };
    setFilters(newFilters);
    fetchPrompts(newFilters);
  }, [filters, fetchPrompts]);

  // Filter by category
  const filterByCategory = useCallback((categoryId: number | null) => {
    const newFilters = { 
      ...filters, 
      category: categoryId || undefined, 
      page: 1 
    };
    setFilters(newFilters);
    fetchPrompts(newFilters);
  }, [filters, fetchPrompts]);

  // Filter by tags
  const filterByTags = useCallback((tags: string[]) => {
    const newFilters = { ...filters, tags, page: 1 };
    setFilters(newFilters);
    fetchPrompts(newFilters);
  }, [filters, fetchPrompts]);

  // Change page
  const changePage = useCallback((page: number) => {
    const newFilters = { ...filters, page };
    setFilters(newFilters);
    fetchPrompts(newFilters);
  }, [filters, fetchPrompts]);

  // Change sorting
  const changeSorting = useCallback((sort: string, order: 'ASC' | 'DESC') => {
    const newFilters = { ...filters, sort, order, page: 1 };
    setFilters(newFilters);
    fetchPrompts(newFilters);
  }, [filters, fetchPrompts]);

  // Clear filters
  const clearFilters = useCallback(() => {
    const newFilters = { page: 1, limit: 20 };
    setFilters(newFilters);
    fetchPrompts(newFilters);
  }, [fetchPrompts]);

  // Initial fetch
  useEffect(() => {
    fetchPrompts();
  }, []);

  return {
    prompts,
    loading,
    error,
    pagination,
    filters,
    searchPrompts,
    filterByCategory,
    filterByTags,
    changePage,
    changeSorting,
    clearFilters,
    refetch: fetchPrompts,
  };
};